import { useMemo, useRef, useState } from 'react';

// Matches the edit service's FLEET_SUM_TOLERANCE: shares must already total 100.
export const FLEET_SUM_TOLERANCE = 0.1;

const moves = (id) => id.replace(/^st/, '');

/** Rows that differ from the metro's own mix are a custom fleet; rows equal to it are
 *  the stock fleet and are not sent, so an untouched editor reruns nothing. */
export function fleetState(rows, stockDefault) {
  const shares = {};
  rows.forEach((r) => { shares[r.id] = Number(r.pct); });
  const ids = Object.keys(shares);
  const total = ids.reduce((s, id) => s + (Number.isFinite(shares[id]) ? shares[id] : NaN), 0);
  const errors = [];
  if (ids.some((id) => !Number.isFinite(shares[id]) || shares[id] < 0)) {
    errors.push('Every share must be a number of 0 or more.');
  } else if (ids.length && Math.abs(total - 100) > FLEET_SUM_TOLERANCE) {
    errors.push(`Shares add up to ${total.toFixed(1)}% — they must total 100%.`);
  }
  const isStock = !!stockDefault
    && ids.length === Object.keys(stockDefault).length
    && ids.every((id) => stockDefault[id] != null
      && Math.abs(shares[id] - stockDefault[id]) < 1e-9);
  const custom = ids.length > 0 && !isStock;
  return { shares, total, errors: custom ? errors : [], custom };
}

/** A search box over the vehicle types, then one share row per chosen type. */
export default function FleetMix({
  catalog, rows, onRows, routed, onCapacityHelp,
}) {
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const byId = useMemo(
    () => Object.fromEntries((catalog?.types || []).map((t) => [t.id, t])),
    [catalog],
  );
  const chosen = new Set(rows.map((r) => r.id));
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const options = (catalog?.types || []).filter((t) => {
    if (chosen.has(t.id)) return false;
    const hay = `${t.label} ${moves(t.id)} ${t.vClass}`.toLowerCase();
    return tokens.every((tok) => hay.includes(tok));
  });

  const add = (t) => {
    onRows([...rows, { id: t.id, pct: '0' }]);
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
  };
  const setPct = (id, pct) => onRows(rows.map((r) => (r.id === id ? { ...r, pct } : r)));
  const remove = (id) => onRows(rows.filter((r) => r.id !== id));
  const reset = () => onRows(Object.entries(catalog.default)
    .map(([id, pct]) => ({ id, pct: String(pct) })));

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setListOpen(true);
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && listOpen && options[active]) {
      e.preventDefault();
      add(options[active]);
    } else if (e.key === 'Escape') {
      setListOpen(false);
    }
  };

  const { total, errors, custom } = fleetState(rows, catalog?.default);
  const totalOk = Math.abs(total - 100) <= FLEET_SUM_TOLERANCE;
  const routedTotal = routed
    ? Object.values(routed).reduce((s, n) => s + n, 0) : 0;

  if (!catalog) {
    return <div className="ne-demand-hint">Loading vehicle types…</div>;
  }

  return (
    <div className="ne-demand-block ne-fleet">
      <div className="ne-demand-block-head">
        <span className="ne-section-label">Vehicle mix</span>
        {catalog.default && custom && (
          <button className="ne-link ne-demand-fmt-toggle" onClick={reset}>
            Reset to default
          </button>
        )}
      </div>

      {!catalog.default && (
        <div className="ne-demand-hint">
          This run uses the legacy mix
          {' '}
          {Object.entries(catalog.stock).map(([id, p]) => `${id} ${p}%`).join(' / ')}.
          A custom mix is built from the vehicle types below.
        </div>
      )}

      <div className="ne-fleet-search">
        <input ref={inputRef} type="text" value={query}
          placeholder="Add a vehicle type — e.g. truck, bus, 61"
          role="combobox" aria-expanded={listOpen} aria-controls="ne-fleet-options"
          onChange={(e) => { setQuery(e.target.value); setActive(0); setListOpen(true); }}
          onFocus={() => setListOpen(true)}
          onBlur={() => setTimeout(() => setListOpen(false), 120)}
          onKeyDown={onKey} />
        {listOpen && options.length > 0 && (
          <ul id="ne-fleet-options" className="ne-fleet-options" role="listbox">
            {options.map((t, i) => (
              <li key={t.id} role="option" aria-selected={i === active}
                className={i === active ? 'active' : ''}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); add(t); }}>
                <span>{t.label}</span>
                <span className="ne-fleet-opt-meta">{moves(t.id)} · {t.vClass}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {rows.map((r) => {
        const t = byId[r.id];
        return (
          <div key={r.id} className="ne-fleet-row">
            <span className="ne-fleet-name" title={`MOVES ${moves(r.id)} · ${t?.vClass} · pce ${t?.pce}`}>
              {t?.label || r.id}
              <span className="ne-fleet-opt-meta"> {moves(r.id)}</span>
            </span>
            <input type="number" min="0" max="100" step="0.1" value={r.pct}
              aria-label={`${t?.label || r.id} share`}
              onChange={(e) => setPct(r.id, e.target.value)} />
            <span>%</span>
            <button className="ne-link ne-fleet-remove" aria-label={`Remove ${t?.label || r.id}`}
              onClick={() => remove(r.id)}>✕</button>
            {t && t.vClass !== 'passenger' && (
              <div className="ne-fleet-row-note">
                The network keeps roads open to passenger cars; a {t.vClass} with no
                permitted route is dropped. Check the routed count after the run.
              </div>
            )}
          </div>
        );
      })}

      {rows.length > 0 && (
        <div className={`ne-fleet-total ${totalOk ? 'ok' : 'bad'}`}>
          <span>Total</span>
          <b>{total.toFixed(1)}%</b>
        </div>
      )}
      {errors.length > 0 && <div className="ne-demand-errors">{errors[0]}</div>}

      <div className="ne-demand-hint">
        Each type&apos;s pce comes with it: 1.0 for four-tyre vehicles, 2.0 for
        buses, heavier trucks and motor homes.{' '}
        <button className="ne-link" onClick={onCapacityHelp}>How this works</button>
      </div>

      {routed && routedTotal > 0 && (
        <div className="ne-demand-hint ne-fleet-routed">
          Routed in the current baseline:{' '}
          {Object.entries(routed).map(([id, n]) => (
            <span key={id} className={n === 0 ? 'zero' : ''}>
              {(byId[id]?.label || id)} {n.toLocaleString()}
              {' '}({((100 * n) / routedTotal).toFixed(1)}%)
            </span>
          )).reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
        </div>
      )}
    </div>
  );
}
